import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Chart as ChartJS, CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Filler,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import { getCategoryData, getSettings } from '../api'
import { useMode } from '../context/ModeContext'
import { useBusinessContext } from '../context/BusinessContext'
import { snapshotPage, popPageSnapshot, restoreScroll } from '../utils/pageState'
import { TransactionsTable } from '../components/txDisplay'
import type { CategoryInsight, Transaction } from '../types'

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Tooltip, Filler)

const money = (v: number) => '$' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
const PALETTE = ['#6366f1','#8b5cf6','#ec4899','#f43f5e','#f97316','#f59e0b','#10b981','#06b6d4','#3b82f6','#84cc16']

function catColor(name: string | null | undefined): string {
  if (!name) return '#94a3b8'
  let h = 0
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) & 0x7fffffff
  return PALETTE[h % PALETTE.length]
}

type Period   = 'last12' | 'all' | number
type MonthSel = { year: number; month: number } | null
type TxSortF  = 'date' | 'merchant' | 'details' | 'category' | 'tag' | 'card' | 'charges'
type SortDir  = 'asc' | 'desc'
interface MonthPill { year: number; month: number; label: string }

const todayGlobal = new Date()

// Returns true if a transaction belongs to the given category —
// either as its primary category or as any line of a split transaction.
function matchesCat(t: Transaction, cat: string): boolean {
  return t.category === cat || (t.splitLines?.some(l => l.category === cat) ?? false)
}

// Returns true if a transaction belongs to the given subcategory —
// either as its primary subcategory or as any line of a split transaction.
function matchesSubCat(t: Transaction, sub: string): boolean {
  return t.subCategory === sub || (t.splitLines?.some(l => l.subCategory === sub) ?? false)
}

// Returns only the amount attributable to the given category.
// For split transactions, sums only the matching split lines.
function catAttributedAmount(t: Transaction, cat: string): number {
  if (t.isSplit && t.splitLines && t.splitLines.length > 0) {
    return t.splitLines
      .filter(l => l.category === cat)
      .reduce((s, l) => s + Math.abs(l.amount), 0)
  }
  return Math.abs(t.charges ?? 0)
}

function computeMonthPills(period: Period): MonthPill[] {
  if (period === 'all') return []
  if (period === 'last12') {
    const pills: MonthPill[] = []
    for (let i = 12; i >= 0; i--) {
      const d  = new Date(todayGlobal.getFullYear(), todayGlobal.getMonth() - i, 1)
      const yr = d.getFullYear()
      const mo = d.getMonth() + 1
      pills.push({ year: yr, month: mo, label: MONTH_NAMES[mo - 1] + " '" + String(yr).slice(2) })
    }
    return pills
  }
  const year     = period as number
  const maxMonth = year === todayGlobal.getFullYear() ? todayGlobal.getMonth() + 1 : 12
  return Array.from({ length: maxMonth }, (_, i) => ({ year, month: i + 1, label: MONTH_NAMES[i] }))
}

function RankBadge({ change }: { change: number | null }) {
  if (change == null) return (
    <span style={{ fontSize: '0.68rem', fontWeight: 700, color: '#3b82f6', background: 'rgba(59,130,246,0.1)', borderRadius: 4, padding: '0.1rem 0.35rem' }}>NEW</span>
  )
  if (change > 0) return <span className="pb-badge-up" style={{ fontSize: '0.68rem' }}><i className="bi bi-arrow-up" />{change}</span>
  if (change < 0) return <span className="pb-badge-down" style={{ fontSize: '0.68rem' }}><i className="bi bi-arrow-down" />{Math.abs(change)}</span>
  return <span style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{'—'}</span>
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CHART_OPTS: any = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#1e293b', padding: 10, cornerRadius: 10,
      callbacks: { label: (ctx: { raw: unknown }) => money(ctx.raw as number) },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { color: '#94a3b8', font: { size: 10 } } },
    y: {
      beginAtZero: true, grid: { color: 'rgba(148,163,184,0.15)' }, border: { display: false },
      ticks: { color: '#94a3b8', font: { size: 10 }, callback: (v: unknown) => '$' + ((v as number) >= 1000 ? ((v as number) / 1000).toFixed(0) + 'k' : v) },
    },
  },
}

interface CatPageState {
  period: Period; selectedMonth: MonthSel
  selectedCat: string | null; selectedSubCat: string | null; selectedMerchant: string | null
  txSort: TxSortF; txSortDir: SortDir; showEmpty: boolean
}

export default function Categories() {
  const navigate = useNavigate()
  const { mode } = useMode()
  const { selectedBusinessId } = useBusinessContext()
  const isPersonal = mode === 'personal'
  const bizId      = mode === 'business' ? selectedBusinessId : null
  const autoSelectRef = useRef(true) // auto-select first cat on load / period change

  const [period,           setPeriod]           = useState<Period>('last12')
  const [selectedMonth,    setSelectedMonth]    = useState<MonthSel>({ year: todayGlobal.getFullYear(), month: todayGlobal.getMonth() + 1 })
  const [selectedCat,      setSelectedCat]      = useState<string | null>(null)
  const [selectedSubCat,   setSelectedSubCat]   = useState<string | null>(null)
  const [selectedMerchant, setSelectedMerchant] = useState<string | null>(null)
  const [txSort,           setTxSort]           = useState<TxSortF>('date')
  const [txSortDir,        setTxSortDir]        = useState<SortDir>('desc')
  const [pendingScroll,    setPendingScroll]    = useState<number | null>(null)
  const [showEmpty,        setShowEmpty]        = useState(false)
  const [chartOffset,      setChartOffset]      = useState(0)

  useEffect(() => {
    const snap = popPageSnapshot<CatPageState>('categories')
    if (!snap) return
    const s = snap.state
    setPeriod(s.period); setSelectedMonth(s.selectedMonth)
    setSelectedCat(s.selectedCat); setSelectedSubCat(s.selectedSubCat); setSelectedMerchant(s.selectedMerchant)
    setTxSort(s.txSort); setTxSortDir(s.txSortDir)
    setShowEmpty(s.showEmpty ?? false)
    setPendingScroll(snap.scroll)
    autoSelectRef.current = false // back-nav: keep restored selection
  }, [])

  const monthPills  = useMemo(() => computeMonthPills(period), [period])
  const isAllPeriod = period === 'all'
  const apiYear     = isAllPeriod ? undefined : period === 'last12' ? selectedMonth?.year : (period as number)
  const apiMonth    = selectedMonth?.month

  const { data, isLoading, error } = useQuery({
    queryKey: isAllPeriod ? ['categories', 'all'] : ['categories', apiYear, apiMonth],
    queryFn:  () => getCategoryData(apiYear, apiMonth, isAllPeriod, isPersonal, bizId),
  })

  // Settings — all system-defined categories (for the empty toggle)
  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn:  () => getSettings(),
    staleTime: 5 * 60_000,
  })

  // All-time data — for empty categories chart + fallback
  const { data: last12Data } = useQuery({
    queryKey: ['categories', 'all'],
    queryFn:  () => getCategoryData(undefined, undefined, true, isPersonal, bizId),
    staleTime: 5 * 60_000,
  })

  // When a specific month is selected, allTxns only has that single month.
  // Fetch the full year (and prior year) so the chart window has real data.
  const chartRefYearNum = selectedMonth?.year
    ?? (typeof period === 'number' ? (period as number) : todayGlobal.getFullYear())
  const { data: chartYearData } = useQuery({
    queryKey: ['categories', chartRefYearNum, undefined],
    queryFn:  () => getCategoryData(chartRefYearNum, undefined, false, isPersonal, bizId),
    enabled:  selectedMonth != null && !showEmpty,
    staleTime: 5 * 60_000,
  })
  const { data: chartPriorYearData } = useQuery({
    queryKey: ['categories', chartRefYearNum - 1, undefined],
    queryFn:  () => getCategoryData(chartRefYearNum - 1, undefined, false, isPersonal, bizId),
    enabled:  selectedMonth != null && !showEmpty,
    staleTime: 5 * 60_000,
  })
  const { data: chartPrior2YearData } = useQuery({
    queryKey: ['categories', chartRefYearNum - 2, undefined],
    queryFn:  () => getCategoryData(chartRefYearNum - 2, undefined, false, isPersonal, bizId),
    enabled:  selectedMonth != null && !showEmpty,
    staleTime: 5 * 60_000,
  })
  // Auto-select the first category after data loads or period changes
  useEffect(() => {
    if (!data || showEmpty) return
    if (autoSelectRef.current && data.categories.length > 0) {
      setSelectedCat(data.categories[0].name)
      autoSelectRef.current = false
    }
  }, [data, showEmpty])


  const allTxns = (data?.transactions ?? []) as Transaction[]

  // Categories that exist in settings but had 0 transactions in the current period
  const emptyCategories = useMemo(() => {
    if (!settings || !data) return []
    const activeNames = new Set(data.categories.map(c => c.name))
    return settings.categories
      .filter(c => !activeNames.has(c.name))
      .map(c => ({
        name:  c.name,
        icon:  c.icon  ?? 'bi-tag',
        color: c.color ?? catColor(c.name),
      }))
  }, [settings, data])

  // The "end month" of the 12-month chart window, derived from the selected period
  // e.g. if viewing June 2024, the window is Jul 2023 → Jun 2024
  const chartRefYear  = selectedMonth?.year
    ?? (typeof period === 'number' ? (period as number) : todayGlobal.getFullYear())
  const chartRefMonth = selectedMonth?.month
    ?? (typeof period === 'number'
      ? ((period as number) === todayGlobal.getFullYear() ? todayGlobal.getMonth() + 1 : 12)
      : todayGlobal.getMonth() + 1)

  // Builds the ordered array of 12 "YYYY-MM" strings ending at chartRefYear/chartRefMonth
  function buildChartMonths(refYear: number, refMonth: number, offset = 0): string[] {
    const months: string[] = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date(refYear, refMonth - 1 - i + offset, 1)
      months.push(d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'))
    }
    return months
  }

  // Line chart — fixed 12-month window ending at the selected period's last month
  const chartData = useMemo(() => {
    if (!selectedCat) return null
    // Source selection:
    // - showEmpty: last12Data (empty cats have no data in current period)
    // - specific month selected: combined full-year + prior-year data covers any 12-month window
    // - full period (year or last-12, no month): allTxns already has the complete period
    const source: Transaction[] = showEmpty
      ? (last12Data?.transactions ?? []) as Transaction[]
      : selectedMonth != null
        ? [
            ...((chartYearData?.transactions ?? []) as Transaction[]),
            ...((chartPriorYearData?.transactions ?? []) as Transaction[]),
            ...((chartPrior2YearData?.transactions ?? []) as Transaction[]),
          ]
        : allTxns
    let txns = source.filter(t => matchesCat(t, selectedCat))
    if (selectedSubCat)   txns = txns.filter(t => matchesSubCat(t, selectedSubCat))
    if (selectedMerchant) txns = txns.filter(t => t.merchantDisplayName === selectedMerchant)

    const months = buildChartMonths(chartRefYear, chartRefMonth, chartOffset)

    const map = new Map<string, number>()
    for (const t of txns) {
      if (!t.date) continue
      const ym = t.date.slice(0, 7)
      map.set(ym, (map.get(ym) ?? 0) + catAttributedAmount(t, selectedCat))
    }

    const color = catColor(selectedCat)
    return {
      labels: months.map(ym => {
        const [y, m] = ym.split('-')
        return MONTH_NAMES[parseInt(m, 10) - 1] + " '" + String(y).slice(2)
      }),
      datasets: [{
        label: selectedCat,
        data: months.map(ym => map.get(ym) ?? 0),
        borderColor: color,
        backgroundColor: color + '22',
        fill: true, tension: 0.4,
        pointRadius: 3, pointHoverRadius: 6,
        borderWidth: 2,
        pointBackgroundColor: color,
      }],
    }
  }, [last12Data, chartYearData, chartPriorYearData, chartPrior2YearData, allTxns, selectedCat, selectedSubCat, selectedMerchant, showEmpty, selectedMonth, chartRefYear, chartRefMonth, chartOffset])

  // Total spent over the 12-month chart window — used for No Activity category header
  const chart12mTotal = useMemo(() => {
    if (!selectedCat || !showEmpty) return 0
    const source = (last12Data?.transactions ?? []) as Transaction[]
    const months = buildChartMonths(chartRefYear, chartRefMonth, chartOffset)
    const monthSet = new Set(months)
    return source
      .filter(t => matchesCat(t, selectedCat) && t.date && monthSet.has(t.date.slice(0, 7)))
      .reduce((s, t) => s + catAttributedAmount(t, selectedCat), 0)
  }, [last12Data, selectedCat, showEmpty, chartRefYear, chartRefMonth, chartOffset])

  // Min/max months with actual data — uses ALL loaded sources so nav arrows reflect the true data boundaries
  const chartDataRange = useMemo(() => {
    if (!selectedCat) return null
    const source: Transaction[] = [
      ...((last12Data?.transactions ?? []) as Transaction[]),
      ...((chartYearData?.transactions ?? []) as Transaction[]),
      ...((chartPriorYearData?.transactions ?? []) as Transaction[]),
      ...((chartPrior2YearData?.transactions ?? []) as Transaction[]),
      ...allTxns,
    ]
    const months = source.filter(t => matchesCat(t, selectedCat) && t.date).map(t => t.date!.slice(0, 7))
    if (months.length === 0) return null
    return { min: months.reduce((a, b) => a < b ? a : b), max: months.reduce((a, b) => a > b ? a : b) }
  }, [last12Data, chartYearData, chartPriorYearData, chartPrior2YearData, allTxns, selectedCat])

  // Last-12m total per empty category — shown in the No Activity list rows
  const emptycat12mTotals = useMemo(() => {
    const source = (last12Data?.transactions ?? []) as Transaction[]
    const monthSet = new Set(buildChartMonths(chartRefYear, chartRefMonth, 0))
    const map = new Map<string, number>()
    for (const t of source) {
      if (!t.date) continue
      if (!monthSet.has(t.date.slice(0, 7))) continue
      if (t.isSplit && t.splitLines && t.splitLines.length > 0) {
        // For split transactions, attribute each line's amount to its own category.
        for (const line of t.splitLines) {
          if (!line.category) continue
          map.set(line.category, (map.get(line.category) ?? 0) + Math.abs(line.amount))
        }
      } else if (t.category) {
        map.set(t.category, (map.get(t.category) ?? 0) + Math.abs(t.charges ?? 0))
      }
    }
    return map
  }, [last12Data, chartRefYear, chartRefMonth])

  // Merchants derived from transactions, filtered by cat + optionally subcat
  const derivedMerchants = useMemo(() => {
    if (!selectedCat) return []
    let txns = allTxns.filter(t => matchesCat(t, selectedCat))
    if (selectedSubCat) txns = txns.filter(t => matchesSubCat(t, selectedSubCat))
    const map = new Map<string, { totalSpent: number; transactionCount: number }>()
    for (const t of txns) {
      const name = t.merchantDisplayName || '—'
      const ex   = map.get(name) ?? { totalSpent: 0, transactionCount: 0 }
      map.set(name, { totalSpent: ex.totalSpent + catAttributedAmount(t, selectedCat!), transactionCount: ex.transactionCount + 1 })
    }
    return Array.from(map.entries()).map(([name, s]) => ({ name, ...s })).sort((a, b) => b.totalSpent - a.totalSpent).slice(0, 10)
  }, [allTxns, selectedCat, selectedSubCat])

  const sortedTxns = useMemo(() => {
    let txns = [...allTxns]
    if (selectedCat != null)      txns = txns.filter(t => matchesCat(t, selectedCat))
    if (selectedSubCat != null)   txns = txns.filter(t => matchesSubCat(t, selectedSubCat))
    if (selectedMerchant != null) txns = txns.filter(t => t.merchantDisplayName === selectedMerchant)
    return txns.sort((a, b) => {
      let av: string | number = '', bv: string | number = ''
      if      (txSort === 'date')     { av = a.date ?? '';                bv = b.date ?? '' }
      else if (txSort === 'merchant') { av = a.merchantDisplayName ?? ''; bv = b.merchantDisplayName ?? '' }
      else if (txSort === 'details')  { av = a.description ?? '';         bv = b.description ?? '' }
      else if (txSort === 'category') { av = a.category ?? ''; bv = b.category ?? '' }
      else if (txSort === 'tag')      { av = a.tag ?? '';       bv = b.tag ?? '' }
      else if (txSort === 'card')     { av = a.account ?? '';            bv = b.account ?? '' }
      else if (txSort === 'charges')  { av = a.charges ?? 0;   bv = b.charges ?? 0 }
      if (typeof av === 'string') { const c = av.localeCompare(bv as string); return txSortDir === 'asc' ? c : -c }
      return txSortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number)
    })
  }, [allTxns, selectedCat, selectedSubCat, selectedMerchant, txSort, txSortDir])


  const goToEdit = (id: string) => {
    snapshotPage<CatPageState>('categories', { period, selectedMonth, selectedCat, selectedSubCat, selectedMerchant, txSort, txSortDir, showEmpty })
    navigate('/transactions/' + id + '/edit')
  }

  const changePeriod = (p: Period) => { autoSelectRef.current = true; setChartOffset(0); setPeriod(p); setSelectedMonth(null); setSelectedCat(null); setSelectedSubCat(null); setSelectedMerchant(null) }
  const changeMonth  = (m: MonthSel) => { autoSelectRef.current = true; setChartOffset(0); setSelectedMonth(m); setSelectedCat(null); setSelectedSubCat(null); setSelectedMerchant(null) }
  const selectCat    = (name: string) => { setChartOffset(0); setSelectedCat(prev => prev === name ? null : name); setSelectedSubCat(null); setSelectedMerchant(null) }
  const toggleEmpty  = (val: boolean) => { setChartOffset(0); setShowEmpty(val); setSelectedCat(null); setSelectedSubCat(null); setSelectedMerchant(null) }

  if (isLoading) return <div className="pb-spinner-wrap"><div className="spinner-border" style={{ color: 'var(--pb-primary)' }} /></div>
  if (error || !data) return <div className="pb-empty"><i className="bi bi-exclamation-triangle me-2" />Failed to load categories.</div>

  if (pendingScroll != null && !isLoading) { restoreScroll(pendingScroll); setPendingScroll(null) }

  const thisYear   = new Date().getFullYear()
  const earliest   = data.earliestYear ?? thisYear
  const minYear    = Math.max(earliest, thisYear - 5)
  const yearRange: number[] = []
  for (let y = thisYear; y >= minYear; y--) yearRange.push(y)
  const periodBtns: { label: string; value: Period }[] = [
    { label: 'Last 12 Mo', value: 'last12' },
    ...yearRange.map(y => ({ label: String(y), value: y as Period })),
    { label: 'All', value: 'all' },
  ]

  const detailCat: CategoryInsight | null = !showEmpty && selectedCat ? (data.categories.find(c => c.name === selectedCat) ?? null) : null
  const emptyCatMeta = showEmpty && selectedCat ? (settings?.categories.find(c => c.name === selectedCat) ?? null) : null
  const activeDetailColor = detailCat ? (detailCat.color || catColor(detailCat.name)) : catColor(selectedCat)
  const hasFilters = selectedCat != null || selectedSubCat != null || selectedMerchant != null
  const displayList = showEmpty ? emptyCategories : data.categories

  // Label for the 12-month window end — shown in No Activity panel
  const chartRefLabel = MONTH_NAMES[chartRefMonth - 1] + " '" + String(chartRefYear).slice(2)

  // Chart nav arrow state
  const chartWindowMonths = buildChartMonths(chartRefYear, chartRefMonth, chartOffset)
  const canGoBack    = chartDataRange != null && chartWindowMonths[0]  > chartDataRange.min
  const canGoForward = chartDataRange != null && chartWindowMonths[11] < chartDataRange.max

  return (
    <div>
      <h1 className="pb-page-title mb-3">Categories</h1>

      {/* Period selector */}
      <div className="mb-3">
        <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
          {periodBtns.map(b => (
            <button key={String(b.value)} className={'pb-chart-sel-btn' + (period === b.value ? ' active' : '')} onClick={() => changePeriod(b.value)}>{b.label}</button>
          ))}
        </div>
      </div>

      {/* Month pills */}
      {monthPills.length > 0 && (
        <div className="pb-month-pills mb-4">
          <button className={'pb-month-pill' + (selectedMonth === null ? ' active' : '')} onClick={() => changeMonth(null)}>All</button>
          {monthPills.map(p => {
            const isActive = selectedMonth?.year === p.year && selectedMonth?.month === p.month
            return <button key={p.year + '-' + p.month} className={'pb-month-pill' + (isActive ? ' active' : '')} onClick={() => changeMonth({ year: p.year, month: p.month })}>{p.label}</button>
          })}
        </div>
      )}

      {/* KPI strip */}
      <div className="row g-3 mb-4">
        <div className="col-6 col-md-3"><div className="pb-card pb-card-sm"><div className="pb-kpi-label mb-1"><i className="bi bi-arrow-up-circle me-1" />Total Spent</div><div className="pb-kpi-value" style={{ color: 'var(--pb-danger)' }}>{money(data.totalSpent)}</div></div></div>
        <div className="col-6 col-md-3"><div className="pb-card pb-card-sm"><div className="pb-kpi-label mb-1"><i className="bi bi-arrow-down-circle me-1" />Total Earned</div><div className="pb-kpi-value" style={{ color: '#16a34a' }}>{money(data.totalEarned)}</div></div></div>
        <div className="col-6 col-md-3"><div className="pb-card pb-card-sm"><div className="pb-kpi-label mb-1"><i className="bi bi-tags me-1" />Categories</div><div className="pb-kpi-value">{data.categories.length}</div></div></div>
        <div className="col-6 col-md-3"><div className="pb-card pb-card-sm"><div className="pb-kpi-label mb-1"><i className="bi bi-receipt me-1" />Transactions</div><div className="pb-kpi-value">{data.totalTransactions.toLocaleString()}</div></div></div>
      </div>

      {/* Two-column layout */}
      <div className="row g-4 mb-4">

        {/* ── Left: Category list ── */}
        <div className="col-lg-6">
          <div className="pb-card" style={{ padding: 0 }}>
            {/* Header with Active / Empty toggle */}
            <div style={{ padding: '0.85rem 1rem', borderBottom: '1px solid var(--pb-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
              <span className="pb-kpi-label"><i className="bi bi-bar-chart-steps me-1" />Ranked by Spending</span>
              <div className="pb-chart-selector" style={{ display: 'inline-flex' }}>
                <button className={'pb-chart-sel-btn' + (!showEmpty ? ' active' : '')} onClick={() => toggleEmpty(false)}>
                  <i className="bi bi-bar-chart me-1" />Active ({data.categories.length})
                </button>
                <button className={'pb-chart-sel-btn' + (showEmpty ? ' active' : '')} onClick={() => toggleEmpty(true)}>
                  <i className="bi bi-slash-circle me-1" />No activity ({emptyCategories.length})
                </button>
              </div>
            </div>

            {displayList.length === 0 ? (
              <div className="pb-empty" style={{ padding: '2rem' }}>
                {showEmpty ? 'All categories have activity in this period.' : 'No category data for this period.'}
              </div>
            ) : showEmpty ? (
              /* Empty categories list */
              emptyCategories.map(cat => {
                const color    = cat.color
                const isActive = selectedCat === cat.name
                return (
                  <div
                    key={cat.name}
                    onClick={() => selectCat(cat.name)}
                    style={{ display: 'grid', gridTemplateColumns: '2rem 1fr auto', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1rem', borderBottom: '1px solid var(--pb-border)', cursor: 'pointer', background: isActive ? color + '18' : undefined, transition: 'background 0.1s' }}
                    onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.04)' }}
                    onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = '' }}
                  >
                    <span style={{ width: 28, height: 28, borderRadius: 8, flexShrink: 0, background: color + '22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <i className={'bi ' + cat.icon} style={{ color, fontSize: '0.85rem' }} />
                    </span>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: isActive ? 'var(--pb-primary)' : 'var(--pb-text)' }}>
                        {cat.name}
                        {isActive && <i className="bi bi-check-circle-fill ms-1" style={{ color: 'var(--pb-primary)', fontSize: '0.72rem' }} />}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>No transactions this period</div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{money(emptycat12mTotals.get(cat.name) ?? 0)}</div>
                      <div style={{ fontSize: '0.68rem', color: 'var(--pb-muted)' }}>Last 12 mo</div>
                    </div>
                  </div>
                )
              })
            ) : (
              /* Active categories list */
              data.categories.map((cat, idx) => {
                const isIncome = cat.name === 'Income'
                const color    = isIncome ? '#16a34a' : (cat.color || catColor(cat.name))
                const isActive = selectedCat === cat.name
                return (
                  <div
                    key={cat.name}
                    onClick={() => selectCat(cat.name)}
                    style={{ display: 'grid', gridTemplateColumns: '2rem 1.8rem 1fr auto', alignItems: 'center', gap: '0.5rem', padding: '0.65rem 1rem', borderBottom: '1px solid var(--pb-border)', cursor: 'pointer', background: isActive ? color + '18' : undefined, transition: 'background 0.1s' }}
                    onMouseEnter={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.04)' }}
                    onMouseLeave={e => { if (!isActive) (e.currentTarget as HTMLElement).style.background = '' }}
                  >
                    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--pb-muted)', textAlign: 'right' }}>
                      {isIncome ? <i className="bi bi-arrow-down-circle" style={{ color }} /> : `#${idx + 1}`}
                    </span>
                    <span style={{ display: 'flex', justifyContent: 'center' }}>{isIncome ? null : <RankBadge change={cat.rankChange} />}</span>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem', color: isActive ? color : isIncome ? color : 'var(--pb-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {cat.name}
                        {isActive && <i className="bi bi-check-circle-fill ms-1" style={{ color, fontSize: '0.72rem' }} />}
                      </div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{cat.transactionCount} txns &middot; {isIncome ? 'earned' : `${money(cat.avgPerMonth)}/mo`}</div>
                      <div style={{ marginTop: 4, height: 3, borderRadius: 4, background: 'var(--pb-border)', overflow: 'hidden' }}>
                        <div style={{ width: Math.min(cat.percentage, 100) + '%', height: '100%', background: color, borderRadius: 4 }} />
                      </div>
                    </div>
                    <div style={{ textAlign: 'right', flexShrink: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem', color: isIncome ? '#16a34a' : undefined }}>{money(cat.totalSpent)}</div>
                      <div style={{ fontSize: '0.72rem', color: 'var(--pb-muted)' }}>{cat.percentage.toFixed(1)}%</div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* ── Right: Detail panel ── */}
        <div className="col-lg-6">
          <div className="pb-card" style={{ minHeight: 200 }}>

            {/* Nothing selected */}
            {selectedCat == null && (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 180, color: 'var(--pb-muted)' }}>
                <i className="bi bi-hand-index" style={{ fontSize: '2rem', opacity: 0.3, marginBottom: '0.75rem' }} />
                <div style={{ fontSize: '0.88rem' }}>Select a category to view details</div>
              </div>
            )}

            {/* Empty category selected */}
            {showEmpty && emptyCatMeta && selectedCat && (
              <div>
                {/* Header — matches active category style, with last-12m total */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                  <span style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: (emptyCatMeta.color ?? catColor(emptyCatMeta.name)) + '22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={'bi ' + (emptyCatMeta.icon ?? 'bi-tag')} style={{ color: emptyCatMeta.color ?? catColor(emptyCatMeta.name), fontSize: '1.2rem' }} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{emptyCatMeta.name}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--pb-muted)', marginTop: 3 }}>No transactions in this period</div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: '1.25rem' }}>{money(chart12mTotal)}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--pb-muted)' }}>Last 12 mo as of {chartRefLabel}</div>
                  </div>
                </div>

                {/* Chart — 12-month window ending at chartRefEnd */}
                {chartData ? (
                  <div style={{ marginBottom: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--pb-muted)' }}>
                        Spending over time — last 12 months as of {chartRefLabel}
                      </div>
                      <div style={{ display: 'flex', gap: 2 }}>
                        <button onClick={() => setChartOffset(o => o - 6)} disabled={!canGoBack} style={{ background: 'none', border: 'none', padding: '0 4px', cursor: canGoBack ? 'pointer' : 'default', color: canGoBack ? 'var(--pb-text)' : 'var(--pb-border)', lineHeight: 1 }}><i className="bi bi-chevron-left" style={{ fontSize: '0.85rem' }} /></button>
                        <button onClick={() => setChartOffset(o => o + 6)} disabled={!canGoForward} style={{ background: 'none', border: 'none', padding: '0 4px', cursor: canGoForward ? 'pointer' : 'default', color: canGoForward ? 'var(--pb-text)' : 'var(--pb-border)', lineHeight: 1 }}><i className="bi bi-chevron-right" style={{ fontSize: '0.85rem' }} /></button>
                      </div>
                    </div>
                    <div style={{ height: 150 }}><Line data={chartData} options={CHART_OPTS} /></div>
                  </div>
                ) : (
                  <div style={{ fontSize: '0.82rem', color: 'var(--pb-muted)', textAlign: 'center', padding: '1.5rem 0' }}>No historical data found.</div>
                )}
              </div>
            )}

            {/* Active category selected */}
            {!showEmpty && detailCat && (
              <div>
                {/* Header */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
                  <span style={{ width: 44, height: 44, borderRadius: 12, flexShrink: 0, background: activeDetailColor + '22', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <i className={'bi ' + detailCat.icon} style={{ color: activeDetailColor, fontSize: '1.2rem' }} />
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>{detailCat.name}</div>
                    <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: 3, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '0.78rem', color: 'var(--pb-muted)' }}>{detailCat.transactionCount} transactions</span>
                      {detailCat.vsPriorYear != null && (
                        <span className={detailCat.vsPriorYear <= 0 ? 'pb-badge-up' : 'pb-badge-down'} style={{ fontSize: '0.7rem' }}>
                          <i className={'bi ' + (detailCat.vsPriorYear <= 0 ? 'bi-arrow-down' : 'bi-arrow-up')} />
                          YoY {Math.abs(detailCat.vsPriorYear).toFixed(1)}%
                        </span>
                      )}
                      <RankBadge change={detailCat.rankChange} />
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', flexShrink: 0 }}>
                    <div style={{ fontWeight: 800, fontSize: '1.25rem', color: detailCat.name === 'Income' ? '#16a34a' : undefined }}>{money(detailCat.totalSpent)}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--pb-muted)' }}>{detailCat.percentage.toFixed(1)}% of {detailCat.name === 'Income' ? 'earned' : 'total'}</div>
                  </div>
                </div>

                {/* Spending chart — 12-month window ending at chartRefEnd */}
                {chartData && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--pb-muted)' }}>
                        Spending over time
                        {(selectedSubCat || selectedMerchant) && <span style={{ fontWeight: 400, textTransform: 'none', marginLeft: 6 }}>— filtered</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 2 }}>
                        <button onClick={() => setChartOffset(o => o - 6)} disabled={!canGoBack} style={{ background: 'none', border: 'none', padding: '0 4px', cursor: canGoBack ? 'pointer' : 'default', color: canGoBack ? 'var(--pb-text)' : 'var(--pb-border)', lineHeight: 1 }}><i className="bi bi-chevron-left" style={{ fontSize: '0.85rem' }} /></button>
                        <button onClick={() => setChartOffset(o => o + 6)} disabled={!canGoForward} style={{ background: 'none', border: 'none', padding: '0 4px', cursor: canGoForward ? 'pointer' : 'default', color: canGoForward ? 'var(--pb-text)' : 'var(--pb-border)', lineHeight: 1 }}><i className="bi bi-chevron-right" style={{ fontSize: '0.85rem' }} /></button>
                      </div>
                    </div>
                    <div style={{ height: 150 }}><Line data={chartData} options={CHART_OPTS} /></div>
                  </div>
                )}

                {/* Subcategories */}
                {detailCat.subCategories.length > 0 && (
                  <div style={{ marginBottom: '1.25rem' }}>
                    <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--pb-muted)', marginBottom: 10 }}>Subcategories</div>
                    {detailCat.subCategories.map(sub => {
                      const isSubActive = selectedSubCat === sub.name
                      return (
                        <div
                          key={sub.name}
                          onClick={() => { setChartOffset(0); const next = selectedSubCat === sub.name ? null : sub.name; setSelectedSubCat(next); setSelectedMerchant(null) }}
                          style={{ marginBottom: 6, cursor: 'pointer', borderRadius: 6, padding: '0.35rem 0.5rem', background: isSubActive ? activeDetailColor + '18' : undefined, border: isSubActive ? '1.5px solid ' + activeDetailColor + '66' : '1.5px solid transparent', transition: 'all 0.1s' }}
                          onMouseEnter={e => { if (!isSubActive) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.04)' }}
                          onMouseLeave={e => { if (!isSubActive) (e.currentTarget as HTMLElement).style.background = '' }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                            <span style={{ fontSize: '0.85rem', fontWeight: isSubActive ? 700 : 500, color: isSubActive ? 'var(--pb-primary)' : undefined }}>
                              {sub.name}
                              {isSubActive && <i className="bi bi-check-circle-fill ms-1" style={{ color: 'var(--pb-primary)', fontSize: '0.68rem' }} />}
                            </span>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                              <span style={{ fontSize: '0.75rem', color: 'var(--pb-muted)' }}>{sub.transactionCount} txns</span>
                              <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{money(sub.totalSpent)}</span>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ flex: 1, height: 5, borderRadius: 4, background: 'var(--pb-border)', overflow: 'hidden' }}>
                              <div style={{ width: Math.min(sub.percentage, 100) + '%', height: '100%', background: activeDetailColor, borderRadius: 4 }} />
                            </div>
                            <span style={{ fontSize: '0.72rem', color: 'var(--pb-muted)', minWidth: 36, textAlign: 'right' }}>{sub.percentage.toFixed(1)}%</span>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                )}

                {/* Top Merchants */}
                <div>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--pb-muted)', marginBottom: 10 }}>
                    Top Merchants
                    {selectedSubCat && <span style={{ marginLeft: 6, fontWeight: 400, textTransform: 'none' }}>({selectedSubCat})</span>}
                  </div>
                  {derivedMerchants.length === 0
                    ? <div style={{ fontSize: '0.82rem', color: 'var(--pb-muted)' }}>{'—'}</div>
                    : derivedMerchants.map((m, mi) => {
                      const isMerchActive = selectedMerchant === m.name
                      return (
                        <div
                          key={m.name}
                          onClick={() => { setChartOffset(0); setSelectedMerchant(prev => prev === m.name ? null : m.name) }}
                          style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.35rem 0.5rem', marginBottom: 2, borderRadius: 6, cursor: 'pointer', background: isMerchActive ? activeDetailColor + '18' : undefined, border: isMerchActive ? '1.5px solid ' + activeDetailColor + '66' : '1.5px solid transparent', transition: 'all 0.1s' }}
                          onMouseEnter={e => { if (!isMerchActive) (e.currentTarget as HTMLElement).style.background = 'rgba(99,102,241,0.04)' }}
                          onMouseLeave={e => { if (!isMerchActive) (e.currentTarget as HTMLElement).style.background = '' }}
                        >
                          <span style={{ fontSize: '0.72rem', color: 'var(--pb-muted)', width: 18, textAlign: 'right', flexShrink: 0 }}>{mi + 1}</span>
                          <span style={{ flex: 1, fontSize: '0.85rem', fontWeight: isMerchActive ? 700 : 500, color: isMerchActive ? 'var(--pb-primary)' : undefined, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {m.name}
                            {isMerchActive && <i className="bi bi-check-circle-fill ms-1" style={{ color: 'var(--pb-primary)', fontSize: '0.68rem' }} />}
                          </span>
                          <span style={{ fontSize: '0.72rem', color: 'var(--pb-muted)', flexShrink: 0 }}>{m.transactionCount}&times;</span>
                          <span style={{ fontSize: '0.85rem', fontWeight: 600, flexShrink: 0 }}>{money(m.totalSpent)}</span>
                        </div>
                      )
                    })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Transaction table */}
      <TransactionsTable
        items={sortedTxns as Transaction[]}
        showPB
        stateKey="categories"
        resetKey={`${selectedCat ?? ''}|${selectedSubCat ?? ''}|${selectedMerchant ?? ''}`}
        onRowClick={goToEdit}
        headerExtra={hasFilters ? (
          <>
            {selectedCat != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--pb-primary)', background: 'rgba(99,102,241,0.1)', borderRadius: 20, padding: '0.2rem 0.65rem' }}>
                <span style={{ display: 'inline-block', width: 7, height: 7, borderRadius: '50%', background: catColor(selectedCat) }} />{selectedCat}
                <button onClick={() => { setSelectedCat(null); setSelectedSubCat(null); setSelectedMerchant(null) }} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pb-muted)', cursor: 'pointer', lineHeight: 1 }}><i className="bi bi-x" /></button>
              </span>
            )}
            {selectedSubCat != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--pb-primary)', background: 'rgba(99,102,241,0.1)', borderRadius: 20, padding: '0.2rem 0.65rem' }}>
                <i className="bi bi-tag" style={{ fontSize: '0.75rem' }} />{selectedSubCat}
                <button onClick={() => { setSelectedSubCat(null); setSelectedMerchant(null) }} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pb-muted)', cursor: 'pointer', lineHeight: 1 }}><i className="bi bi-x" /></button>
              </span>
            )}
            {selectedMerchant != null && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--pb-primary)', background: 'rgba(99,102,241,0.1)', borderRadius: 20, padding: '0.2rem 0.65rem' }}>
                <i className="bi bi-shop" style={{ fontSize: '0.75rem' }} />{selectedMerchant}
                <button onClick={() => setSelectedMerchant(null)} style={{ background: 'none', border: 'none', padding: 0, color: 'var(--pb-muted)', cursor: 'pointer', lineHeight: 1 }}><i className="bi bi-x" /></button>
              </span>
            )}
          </>
        ) : undefined}
      />
    </div>
  )
}
